import { Debt } from '../../../models/Entities/CashFlow/Debt/Debt';
import { Settlement } from '../../../models/Entities/Settlements/Settlement';
import { SettlementStatusEnum } from '../../../models/Enums/SettlementStatusEnum';

/** Selección de liquidaciones para cobrarlas (o pagarlas) en un solo movimiento. */

/** Sólo las que ya tienen deuda generada: confirmadas o con deuda impaga. */
export const canSettle = (settlement: Settlement): boolean =>
    settlement.status === SettlementStatusEnum.CONFIRMED || settlement.status === SettlementStatusEnum.DEBT_UNPAID;

/**
 * Las deudas de cashflow con saldo de una liquidación (si ya se cargaron). La
 * deuda de caja no trae protocolo, así que se le agrega el de la liquidación
 * para que el pago quede como salida de plata de ese protocolo.
 */
export const pendingDebtsOf = (settlement: Settlement): Debt[] =>
    (settlement.debts ?? [])
        .map((item) => item.CashFlowDebt)
        .filter((debt): debt is Debt => !!debt && (debt.pendingAmount ?? 0) > 0 && !debt.isDebtSettled)
        .map((debt) => ({ ...debt, protocolId: debt.protocolId ?? settlement.protocol?.[0]?.protocolId ?? null }));

/** Ya se pidieron las deudas de la liquidación al menos una vez. */
export const debtsLoaded = (settlement: Settlement): boolean =>
    settlement.isGettingDebtsSuccess !== undefined || !!settlement.failOnGettingDebts;

export interface BatchPlan {
    /** 'receiver': el centro cobra; 'payer': el centro paga. */
    direction: 'receiver' | 'payer';
    counterpartId: string;
    debts: Debt[];
    /** Liquidaciones seleccionadas que no aportan deuda con saldo. */
    withoutDebts: number[];
}

/**
 * Arma el cobro de varias liquidaciones. Falla con un motivo si mezclan
 * contrapartes o sentidos (unas a cobrar y otras a pagar).
 */
export const planBatch = (settlements: Settlement[], ownerId: string): { plan?: BatchPlan; error?: string } => {
    const debts: Debt[] = [];
    const withoutDebts: number[] = [];
    settlements.forEach((settlement) => {
        const pending = pendingDebtsOf(settlement);
        if (pending.length === 0) withoutDebts.push(settlement.id);
        debts.push(...pending);
    });

    if (debts.length === 0) return { error: 'Las liquidaciones elegidas no tienen deuda con saldo.' };

    const directions = new Set(debts.map((debt) => (debt.creditor === ownerId ? 'receiver' : 'payer')));
    if (directions.size > 1) return { error: 'Hay liquidaciones a cobrar y a pagar mezcladas; elegí de un solo tipo.' };
    const direction = directions.values().next().value as 'receiver' | 'payer';

    const counterparts = new Set(debts.map((debt) => (direction === 'receiver' ? debt.debtor : debt.creditor)));
    if (counterparts.size > 1) return { error: 'Las liquidaciones son de distintas contrapartes; un cobro es de una sola.' };

    return { plan: { direction, counterpartId: counterparts.values().next().value as string, debts, withoutDebts } };
};
